import React, { useState, useEffect } from "react";
import { withNamespaces } from "react-i18next";
import {
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Paper,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Autocomplete,
  TextField,
  DialogActions,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  Pagination,
  Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import Axios from "axios";
import Sidebar from "../../sidebar/sidebar";
import { companylogo } from "../../../assets/images";
import { BaseUrl } from "../../../Environment";
import moment from "moment";
import { Person } from "@mui/icons-material";
import { getSecureItem } from "../../../utilsFunctions";
import PdfReportCustomerSavings from "./PdfReportCustomerSavings";
import { getCompanyProfile } from "../../../api/index";
import excel from "../../../assets/images/excel.svg";
import pdf from "../../../assets/images/pdf.svg";
import { ExcelReport } from "../ExcelReport";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#e1f0fa",
    color: theme.palette.common.black,
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    cursor: "pointer",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const savingTableHeader = {
  savingId: "Saving ID",
  savingCode: "Saving Code",
  firstName: "Customer",
  totalAmount: "Target Amount",
  totalCollectionAmount: "Deposited",
  totalPenaltyApplied: "Penalty",
  balanceAmount: "Balance",
  pendingDues: "Remaining Dues",
};

const collectionTableHeader = {
  srNo: "Sr. No.",
  dueDate: "Due Date",
  collectedDate: "Collected Date",
  openingBalance: "Opening Balance",
  Installment: "Installment",
  collectionAmount: "Deposited",
  closingBalance: "Closing Balance",
  penaltyApplied: "Penalty",
};

const SavingStatusFilter = [
  { label: "Open", value: "Open" },
  { label: "Closed", value: "Closed" },
  { label: "Cancelled", value: "Cancelled" },
  { label: "All", value: "All" },
];

const PaymentStatusFilter = [
  { label: "Due", value: "Due" },
  { label: "OverDue", value: "OverDue" },
  { label: "All", value: "All" },
];

const TenureFilter = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "All", label: "All" },
];

const CustomerWiseSavingReport = () => {
  const loggedinUser = getSecureItem("loggedinUser");
  const columnWidths = [{ wch: 200 }, { wch: 200 }, { wch: 250 }];
  const currency = getSecureItem("country")?.symbol;
  const userType = loggedinUser?.userType || "";

  const fullReportRef = React.useRef(false);
  const [fullReport, setFullReport] = useState(false);
  const setFullReportSync = (val) => {
    fullReportRef.current = val;
    setFullReport(val);
  };

  const [savingData, setSavingData] = useState([]);
  const [sortedData, setSortedData] = useState([]);
  const [totalSummary, setTotalSummary] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [gotFullData, setGotFullData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);
  const [exportType, setExportType] = useState(null);

  const [savingId, setSavingId] = useState(null);
  const [collectionModal, setCollectionModal] = useState(false);
  const [selectedBorrower, setSelectedBorrower] = useState("");
  const [savingCollectionData, setSavingCollectionData] = useState([]);
  const [savingSummary, setSavingSummary] = useState(null);

  const [showFirstModal, setShowFirstModal] = useState(true);
  const [customerList, setCustomerList] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [customerId, setCustomerId] = useState("");

  const [page, setPage] = useState(1);
  const [savingStatus, setSavingStatus] = useState("Open");
  const [paymentStatus, setPaymentStatus] = useState("All");
  const [tenureType, setTenureType] = useState("All");

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const buildPayload = () => ({
    companyId: getSecureItem("loggedinUserCompany"),
    collectorId: loggedinUser?.userId,
    savingStatus,
    paymentStatus,
    tenureType,
    limit: 20,
    page,
    fullReport: fullReportRef.current,
    userType,
    customerId,
  });

  const fetchCustomerList = async () => {
    try {
      const companyId = getSecureItem("loggedinUserCompany");
      const { data } = await Axios.get(
        `${BaseUrl}/customer/getAllCustomers?name=&companyId=${companyId}`,
        { headers: { Authorization: "Bearer " + getSecureItem("token") } },
      );
      setCustomerList(
        data?.map((item) => ({
          label: item?.cust_id
            ? `${item.firstName} ${item.lastName} (${item.cust_id})`
            : `${item.firstName} ${item.lastName}`,
          value: item.customerId,
        })) || [],
      );
    } catch (err) {
      console.error(err);
    }
  };

  const getSavingData = async () => {
    try {
      setLoading(true);
      const token = getSecureItem("token");
      const res = await Axios.post(
        `${BaseUrl}/savings/getSavingsReport`,
        buildPayload(),
        { headers: { Authorization: "Bearer " + token } },
      );
      setSavingData(res?.data?.result || []);
      setTotalPages(Math.ceil((res?.data?.totalRecords || 0) / 20) || 1);
      if (fullReportRef.current) setGotFullData(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getSavingsSummary = async () => {
    try {
      const token = getSecureItem("token");
      const { data } = await Axios.post(
        `${BaseUrl}/savings/getSavingsSummary`,
        buildPayload(),
        { headers: { Authorization: "Bearer " + token } },
      );
      setTotalSummary(data?.data);
    } catch (err) {
      console.error(err);
    }
  };

  const getSavingCollections = async (id) => {
    try {
      const token = getSecureItem("token");
      const { data } = await Axios.get(
        `${BaseUrl}/savings/getSavingsCollectionBySavingId/?savingId=${id}`,
        { headers: { Authorization: "Bearer " + token } },
      );
      setSavingCollectionData(data?.data?.savingsCollections || []);
      setSavingSummary(data?.data?.summary || null);
    } catch (err) {
      console.error(err);
    }
  };

  const getCompany = async () => {
    try {
      const compData = await getCompanyProfile();
      const {
        profilePic,
        companyName,
        GstIn,
        companyWebsite,
        phone,
        companyMail,
        AddressStreet,
        AddressLandmark,
        AddressCity,
        AddressState,
        AddressPincode,
      } = compData;
      setCompanyData({
        profilePic,
        companyName,
        GstIn,
        companyWebsite,
        phone,
        companyMail,
        address: {
          AddressStreet,
          AddressLandmark,
          AddressCity,
          AddressState,
          AddressPincode,
        },
      });
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getCompany();
    fetchCustomerList();
  }, []);

  useEffect(() => {
    if (!customerId) return;
    getSavingData();
    getSavingsSummary();
  }, [customerId, page, savingStatus, paymentStatus, tenureType, fullReport]);

  useEffect(() => {
    if (!savingData?.length) {
      setSortedData([]);
      return;
    }
    let arr = [...savingData];
    if (sortConfig.key) {
      arr.sort((a, b) => {
        const aVal =
          sortConfig.key === "firstName"
            ? a?.customer?.firstName
            : a[sortConfig.key];
        const bVal =
          sortConfig.key === "firstName"
            ? b?.customer?.firstName
            : b[sortConfig.key];

        if (typeof aVal === "number" && typeof bVal === "number")
          return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
        if (typeof aVal === "string" && typeof bVal === "string")
          return sortConfig.direction === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        return 0;
      });
    }
    setSortedData(arr);
  }, [sortConfig, savingData]);

  useEffect(() => {
    if (!gotFullData || !sortedData.length) return;

    const fourBlockData = buildFourBlockData();

    if (exportType === "pdf") {
      PdfReportCustomerSavings({
        sortedData,
        fourBlockData,
        savingStatus,
        paymentStatus,
        tenureType,
        companyData,
        selectedCustomer,
      });
    }

    if (exportType === "excel") {
      const rows = sortedData.map((item) => ({
        "Saving ID": item.savingId,
        "Saving Code": item.savingCode,
        Customer:
          `${item?.customer?.firstName ?? ""} ${item?.customer?.lastName ?? ""}`.trim() +
          (item?.customer?.cust_id ? ` (${item.customer.cust_id})` : ""),
        "Target Amount": item.totalAmount,
        Deposited: item.totalCollected,
        "Penalty Applied": item.totalPenalty,
        Balance: item.outstandingBalance,
        "Remaining Dues": item.remainingDues,
        Tenure: item.tenureType,
      }));
      ExcelReport({
        data: rows,
        fileName: "customer_wise_saving_report.xlsx",
        sheetName: "Customer-Wise Saving Report",
        columnWidths,
      });
    }

    setTimeout(() => {
      setExportType(null);
      setFullReportSync(false);
      setGotFullData(false);
    }, 100);
  }, [gotFullData]);

  const handleSortNew = (key) =>
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));

  const handlePdfClick = () => {
    setExportType("pdf");
    setFullReportSync(true);
  };

  const handleExcelClick = () => {
    setExportType("excel");
    setFullReportSync(true);
  };

  const buildFourBlockData = () => [
    { heading: "Total Target", value: totalSummary?.totalTarget ?? 0 },
    { heading: "Total Deposited", value: totalSummary?.totalCollected ?? 0 },
    { heading: "Total Penalty", value: totalSummary?.totalPenalty ?? 0 },
    {
      heading: "Outstanding Balance",
      value: totalSummary?.outstandingBalance ?? 0,
    },
  ];

  const buildSavingSummaryData = () => [
    { heading: "Total Deposited", value: savingSummary?.totalCollected ?? 0 },
    { heading: "Total Penalty", value: savingSummary?.totalPenalty ?? 0 },
    {
      heading: "Outstanding Balance",
      value: savingSummary?.outstandingBalance ?? 0,
    },
  ];

  return (
    <>
      <Sidebar />
      <div style={{ marginLeft: "73px", height: "100vh" }}>
        <Grid container direction="column">
          <Grid item sx={{ backgroundColor: "#fff" }}>
            <div style={{ margin: 5 }}>
              <img src={companylogo} width={200} height={40} alt="logo" />
            </div>
          </Grid>

          <strong
            style={{
              textAlign: "center",
              fontFamily: "Lato",
              marginTop: 10,
              fontSize: 18,
            }}
          >
            Customer Wise Saving Report
          </strong>

          <Grid
            item
            container
            paddingX={4}
            paddingY={2}
            spacing={2}
            alignItems="center"
          >
            <Grid item>
              <ToggleButtonGroup
                color="primary"
                value={savingStatus}
                exclusive
                onChange={(_, val) => {
                  if (val) {
                    setPage(1);
                    setSavingStatus(val);
                  }
                }}
                size="small"
              >
                {SavingStatusFilter.map((f, i) => (
                  <ToggleButton key={i} value={f.value}>
                    {f.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>

            <Grid item>
              <ToggleButtonGroup
                color="primary"
                value={paymentStatus}
                exclusive
                onChange={(_, val) => {
                  if (val) {
                    setPage(1);
                    setPaymentStatus(val);
                  }
                }}
                size="small"
              >
                {PaymentStatusFilter.map((f, i) => (
                  <ToggleButton key={i} value={f.value}>
                    {f.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>

            <Grid item>
              <ToggleButtonGroup
                color="primary"
                value={tenureType}
                exclusive
                onChange={(_, val) => {
                  if (val) {
                    setPage(1);
                    setTenureType(val);
                  }
                }}
                size="small"
              >
                {TenureFilter.map((f, i) => (
                  <ToggleButton key={i} value={f.value}>
                    {f.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>

            <Grid item>
              <Button
                variant="outlined"
                style={{ width: 220 }}
                onClick={() => setShowFirstModal(true)}
                startIcon={<Person />}
              >
                Change Customer
              </Button>
            </Grid>
          </Grid>

          <Grid container spacing={2} paddingX={4} paddingY={2}>
            {buildFourBlockData().map(({ heading, value }) => (
              <Grid key={heading} item xs={12} sm={6} md={4} lg={3}>
                <Card elevation={3} sx={{ minHeight: 130, borderRadius: 3 }}>
                  <CardHeader
                    title={<div style={{ fontSize: 15 }}>{heading}</div>}
                  />
                  <CardContent>
                    <div style={{ fontSize: 20, fontWeight: 600 }}>
                      {(value ?? "0") + (currency ? " " + currency : "")}
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {!savingId && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box paddingX={4} paddingY={2}>
                <p className="Fonts">NOTE: Click on record to see details</p>
              </Box>
              <Box paddingX={4} paddingY={2}>
                <Stack direction="row" alignItems="center" gap={2}>
                  <p className="Fonts">Download Reports:</p>
                  <img
                    src={pdf}
                    alt="pdf"
                    className="pdficon"
                    onClick={handlePdfClick}
                  />
                  <img
                    src={excel}
                    alt="excel"
                    className="excelicon"
                    onClick={handleExcelClick}
                  />
                </Stack>
              </Box>
            </div>
          )}

          <Grid item container paddingX={4} paddingY={2}>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    {Object.keys(savingTableHeader).map((property) => (
                      <StyledTableCell key={property}>
                        <TableSortLabel
                          active={sortConfig.key === property}
                          direction={
                            sortConfig.key === property
                              ? sortConfig.direction
                              : "asc"
                          }
                          onClick={() => handleSortNew(property)}
                        >
                          {savingTableHeader[property]}
                        </TableSortLabel>
                      </StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData.map((data, index) => (
                    <StyledTableRow
                      key={index}
                      onClick={() => {
                        setSavingId(data?.savingId);
                        setSelectedBorrower(
                          `${data?.customer?.firstName} ${data?.customer?.lastName}`,
                        );
                        getSavingCollections(data?.savingId);
                        setCollectionModal(true);
                      }}
                    >
                      <StyledTableCell>{data?.savingId}</StyledTableCell>
                      <StyledTableCell>{data?.savingCode}</StyledTableCell>
                      <StyledTableCell>
                        <Stack>
                          <div>
                            {data?.customer?.firstName}{" "}
                            {data?.customer?.lastName}
                          </div>
                          {data?.customer?.cust_id && (
                            <div>({data.customer.cust_id})</div>
                          )}
                        </Stack>
                      </StyledTableCell>
                      <StyledTableCell>{data?.totalAmount}</StyledTableCell>
                      <StyledTableCell>{data?.totalCollected}</StyledTableCell>
                      <StyledTableCell>{data?.totalPenalty}</StyledTableCell>
                      <StyledTableCell>{data?.outstandingBalance}</StyledTableCell>
                      <StyledTableCell>{data?.remainingDues}</StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, newPage) => setPage(newPage)}
              variant="outlined"
              shape="rounded"
              size="large"
              style={{ marginTop: 10 }}
            />
          </Grid>
        </Grid>

        <Dialog
          open={showFirstModal}
          onClose={() => setShowFirstModal(false)}
          aria-labelledby="customer-select-dialog"
        >
          <DialogTitle id="customer-select-dialog">
            Customer-Wise Saving Report
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Choose the Customer from here.
            </DialogContentText>
            <Autocomplete
              disablePortal
              options={customerList}
              sx={{ width: 350, marginTop: 2 }}
              onChange={(_, newValue) => {
                setCustomerId(newValue?.value || "");
                setSelectedCustomer(newValue?.label || "");
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select Customer" />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={() => setShowFirstModal(false)}
            >
              Show Report
            </Button>
          </DialogActions>
        </Dialog>
      </div>

      <Dialog
        open={collectionModal}
        fullWidth
        maxWidth="lg"
        onClose={() => {
          setSavingId(null);
          setCollectionModal(false);
        }}
        aria-labelledby="collection-dialog-title"
      >
        <DialogTitle id="collection-dialog-title">
          {`Saving Collection Table | Customer's Name: ${selectedBorrower}, Saving ID: ${savingId}`}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} paddingY={2}>
            {buildSavingSummaryData().map(({ heading, value }) => (
              <Grid key={heading} item xs={12} sm={6} md={4} lg={3}>
                <Card elevation={3} sx={{ minHeight: 130, borderRadius: 3 }}>
                  <CardHeader
                    title={<div style={{ fontSize: 15 }}>{heading}</div>}
                  />
                  <CardContent>
                    <div style={{ fontSize: 20, fontWeight: 600 }}>
                      {(value ?? "0") + (currency ? " " + currency : "")}
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {savingId && (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    {Object.keys(collectionTableHeader).map((property) => (
                      <StyledTableCell key={property}>
                        <TableSortLabel
                          active={sortConfig.key === property}
                          direction={
                            sortConfig.key === property
                              ? sortConfig.direction
                              : "asc"
                          }
                          onClick={() => handleSortNew(property)}
                        >
                          {collectionTableHeader[property]}
                        </TableSortLabel>
                      </StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {savingCollectionData.map((data, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell>{index + 1}</StyledTableCell>
                      <StyledTableCell>
                        {data?.dueDate
                          ? moment(data.dueDate).format("DD/MM/YYYY")
                          : ""}
                      </StyledTableCell>
                      <StyledTableCell>
                        {data?.collectedDate
                          ? moment(data.collectedDate).format("DD/MM/YYYY")
                          : ""}
                      </StyledTableCell>
                      <StyledTableCell>{data?.openingBalance}</StyledTableCell>
                      <StyledTableCell>{data?.Installment}</StyledTableCell>
                      <StyledTableCell>{data?.collectionAmount}</StyledTableCell>
                      <StyledTableCell>{data?.closingBalance}</StyledTableCell>
                      <StyledTableCell>{data?.penaltyApplied}</StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setSavingId(null);
              setCollectionModal(false);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default withNamespaces()(CustomerWiseSavingReport);